import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import './style.css';
import CCBillModal from '../../Pages/CCbill/CCBillModal.js';
import userService from '../../../shared/services/userService';
import {
  cancelTip as cancelTipAction,
  sendTip as sendTipAction
} from '../../../shared/actions/tipAction';
import {
  charge as chargeTip
} from '../../../shared/actions/ccbillAction';
import {
  showInfo,
  showPaymentLoading,
  showPaymentCancelling,
  confirmDialog,
  closeAlert,
  DEFAULT_CONFIG
} from '../../../shared/utils/swalHandler';
import {
  showTipSentPushToast,
  showTipFailedPushToast
} from '../../../shared/pusNotification/useManualToasts';
import { reportError, reportWarning } from '../../../shared/sentry';
import { buildTipPayload, getVatAndServiceFeeAppliedText } from '../../../shared/utils/paymentUtil';
import { getLocalItem } from '../../../shared/utils/config/localStorage';
import { useModalOpen } from '../../../shared/utils/modalUtils.js';
import { setPaymentFlowActive } from '../../../shared/utils/config/paymentFlowGuard';
import { formatCurrency, formatAmountWithTwoDecimalPlaces } from '../../../shared/utils/function';
import {
  TRANSACTION_TYPE,
  MESSAGES_TIP_PREVIEW_LENGTH,
  TIP_TYPE,
  MIN_VALUE_TIPS,
  MAX_VALUE_TIPS,
  ADM_TIP_PERCENTAGE,
  DEFAULT_PRESET_AMOUNTS
} from '../../../shared/constants/common';

// Constants
const CCBILL_SUBACCOUNT = process.env.REACT_APP_CCBILL_SUBACCOUNT;
const CCBILL_CURRENCY_CODE = Number(process.env.REACT_APP_CCBILL_CURRENCY_CODE);

const SWAL_TIP_CONFIG = {
  ...DEFAULT_CONFIG,
  confirmButtonColor: '#F334BE'
};

const TIP_PAYMENT_FAILED_TITLE = 'Tip Failed';
const TIP_PAYMENT_FAILED_MESSAGE =
  'There was an issue with your payment method. Try again or use a different card.';

function truncateMessage(text, maxLen) {
  if (text.length <= maxLen) return text;
  const truncated = text.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(' ');
  const cut = lastSpace > 50 ? truncated.slice(0, lastSpace) : truncated;
  return `${cut}...`;
}

/**
 * TipModal Component
 * Modal component for sending tips to content creators
 */
const TipModal = ({
  isOpen = false,
  toggle = null,
  item,
  creatorInfo,
  allContent,
  tip_type = TIP_TYPE.CONTENT,
  issubscribed = false,
  onTipSuccess,
  openTipModalRef,
  // coming from mapStateToProps
  user = null,
}) => {
  // State management
  const [tipValue, setTipValue] = useState(MIN_VALUE_TIPS);
  const [totalTipValue, setTotalTipValue] = useState(MIN_VALUE_TIPS);
  const [ccBillModalOpen, setCcBillModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null);
  const [currentTipId, setCurrentTipId] = useState(null);
  const [vatDisplay, setVatDisplay] = useState(0);
  const [vatPercentage, setVatPercentage] = useState(0);
  const [messageExpanded, setMessageExpanded] = useState(false);

  // Refs to track cancellation state
  const cancelTipCalledRef = useRef(false);
  const tipSuccessfulRef = useRef(false);
  const pendingTipResolveRef = useRef(null);

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(isOpen);

  // Block signed-URL refresh (page reload) while tip payment is in progress
  useEffect(() => {
    setPaymentFlowActive(isOpen);
    return () => setPaymentFlowActive(false);
  }, [isOpen]);

  // Computed values (must be before effects that use them)
  const isMessagesTip = useMemo(() => tip_type === TIP_TYPE.MESSAGES, [tip_type]);

  // Message to show: from composer (tip icon with typed message) or pending (e.g. GetNoticedEmptyState "Send with tip")
  const messageWithTip = useMemo(() => {
    const raw =
      (typeof openTipModalRef?.current?.composerMessageForTip === 'string' && openTipModalRef.current.composerMessageForTip) ||
      (typeof openTipModalRef?.current?.pendingMessageWithTip === 'string' && openTipModalRef.current.pendingMessageWithTip) ||
      '';
    return raw.trim();
  }, [isOpen, openTipModalRef]); // re-read when modal opens; ref content is updated by ChatComposer/GetNoticedEmptyState

  const hasMessageWithTip = Boolean(messageWithTip);

  // Reset message preview to collapsed ("show more") whenever modal opens for messages tip
  useEffect(() => {
    if (isOpen && isMessagesTip) {
      setMessageExpanded(false);
    }
  }, [isOpen, isMessagesTip]);

  // Other computed values
  const contentId = useMemo(() => item?._id, [item]);
  const accessToken = getLocalItem('accessToken', null);

  /**
   * Build tip success metadata payload for chat message tips.
   * This is later stringified and sent to Matrix as JSON.
   */
  const buildMessageTipMeta = useCallback(
    (tipData, tipId = null) => {
      if (!isMessagesTip || !tipData) return null;

      const rawMessage =
        messageWithTip ||
        (typeof openTipModalRef?.current?.pendingMessageWithTip === 'string'
          ? openTipModalRef.current.pendingMessageWithTip
          : '') ||
        '';

      const isFirstMessageTipFlag =
        Boolean(tipData.isFirstMessageTip) ||
        Boolean(openTipModalRef?.current?.isFirstMessageTip);

      return {
        message: rawMessage.trim() || null,
        isFirstMessageTip: isFirstMessageTipFlag,
        amount: tipData.amount ?? null,
        vatAmount: tipData.vatAmount ?? null,
        vatPercentage: tipData.vatPercentage ?? null,
        baseAmount: tipData.baseAmount ?? null,
        realPrice: tipData.realPrice ?? null,
        extraCharge: tipData.extraCharge ?? null,
        platformFee: tipData.platformFee ?? null,
        tip_type: tip_type,
        creatorId: creatorInfo?._id ?? null,
        tipId,
      };
    },
    [isMessagesTip, messageWithTip, openTipModalRef, tip_type, creatorInfo]
  );

  /**
   * Extract tip ID from payment form
   * @param {Array|Object} form - Payment form data
   * @returns {string|null} Tip ID or null
   */
  const extractTipId = useCallback((form) => {
    if (!form) return null;

    if (Array.isArray(form)) {
      const tipIdField = form.find(f => f.name === 'c_tip_id');
      return tipIdField?.value || null;
    }

    if (form?.fields && Array.isArray(form.fields)) {
      const tipIdField = form.fields.find(f => f.name === 'c_tip_id');
      return tipIdField?.value || null;
    }

    return null;
  }, []);

  /**
   * Call cancel tip API if interruption occurs
   */
  const callCancelTipAPI = async () => {
    // Double check if tip was successful or already cancelled
    if (cancelTipCalledRef.current || tipSuccessfulRef.current) {
      return;
    }

    cancelTipCalledRef.current = true;

    try {
      const tipId = extractTipId(paymentForm) || currentTipId;

      if (!accessToken) {
        return;
      }

      if (tipId) {
        try {
          await cancelTipAction(tipId);
        } catch (error) {
          reportError(error, 'callCancelTipAPI cancel tip');
        }
      }
    } catch (error) {
      reportError(error, 'callCancelTipAPI');
    }
  };

  // Handle browser close/back button and ESC key
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isOpen) {
        callCancelTipAPI();
      }
    };

    const handlePopState = () => {
      if (isOpen) {
        callCancelTipAPI();
      }
    };

    const handleKeyDown = (event) => {
      if (isOpen && event.key === 'Escape') {
        callCancelTipAPI();
      }
    };

    if (isOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      document.addEventListener('keydown', handleKeyDown);

      // Reset the flags when modal opens
      cancelTipCalledRef.current = false;
      tipSuccessfulRef.current = false;
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, paymentForm, currentTipId, accessToken, extractTipId]);

  /**
   * Cancel tip payment
   * @param {string|null} tipId - Tip ID to cancel
   * @param {boolean} isManualRemove - Optional manual remove flag
   * @param {boolean} shouldCloseModal - If true, close the tip modal after cancel (default true). Set false to return to tip screen.
   */
  const cancelTip = useCallback(async (tipId = null, isManualRemove = false, shouldCloseModal = true) => {
    if (!tipId) {
      setTimeout(() => {
        closeAlert();
      }, 100);
      return false;
    }
    showPaymentCancelling();

    try {
      const response = await cancelTipAction(tipId, isManualRemove);

      if (response.success) {
        closeAlert();
        if (shouldCloseModal && toggle) {
          toggle();
        }
      } else {
        closeAlert();
        showTipFailedPushToast('Error', response.error || 'Failed to cancel tip. Please try again.');
      }
    } catch (error) {
      const status = error?.response?.status;
      const statusCode = status || error?.status;
      const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred';

      if (statusCode === 422 || statusCode === 401) {
        await showInfo('Sorry!', errorMessage, SWAL_TIP_CONFIG).then(() => {
          if (toggle) {
            toggle();
          }
        });
      } else {
        closeAlert();
        showTipFailedPushToast('Error!', 'Failed to cancel tip. Please try again.');
        if (toggle) {
          toggle();
        }
      }
    }
  }, [toggle, cancelTipAction]);

  /**
   * Extract passThroughInfo from payment form
   * @param {Array|Object} form - Payment form data
   * @param {string} contentId - Content ID
   * @param {Object} tipData - Tip data
   * @returns {Array} PassThrough info array
   */
  const extractPassThroughInfo = useCallback((form, contentId, tipData) => {
    let passThroughInfo;

    if (Array.isArray(form)) {
      passThroughInfo = form
        .filter(f => f.name.startsWith('c_'))
        .map(f => ({
          name: f.name,
          value: f.value
        }));
    } else if (form?.fields && Array.isArray(form.fields)) {
      passThroughInfo = form.fields
        .filter(f => f.name.startsWith('c_'))
        .map(f => ({
          name: f.name,
          value: f.value
        }));
    } else {
      const paymentType = tipData.tip_type === TIP_TYPE.MESSAGES ? TRANSACTION_TYPE.MESSAGES_TIP : TRANSACTION_TYPE.TIP;
      passThroughInfo = [
        { name: 'c_user_id', value: localStorage.getItem('userId') || '' },
        { name: 'c_content_id', value: contentId || '' },
        { name: 'c_payment_type', value: paymentType },
        { name: 'c_tip_type', value: tipData.tip_type || TIP_TYPE.CONTENT }
      ];
    }

    // Ensure c_user_id is not empty
    const userId = localStorage.getItem('userId');
    if (userId) {
      const creatorInfoField = passThroughInfo.find(p => p.name === 'c_user_id');
      if (creatorInfoField) {
        creatorInfoField.value = userId;
      } else {
        passThroughInfo.push({ name: 'c_user_id', value: userId });
      }
    }

    return passThroughInfo;
  }, []);

  /**
   * Extract payment form values
   * @param {Array|Object} form - Payment form data
   * @returns {Object} Extracted values
   */
  const extractPaymentFormValues = useCallback((form) => {
    const getFieldValue = (name, defaultValue) => {
      if (Array.isArray(form)) {
        const field = form.find(f => f.name === name);
        return field ? (name === 'currencyCode' || name === 'initialPrice' || name === 'initialPeriod' ? Number(field.value) : field.value) : defaultValue;
      }
      if (form?.fields && Array.isArray(form.fields)) {
        const field = form.fields.find(f => f.name === name);
        return field ? (name === 'currencyCode' || name === 'initialPrice' || name === 'initialPeriod' ? Number(field.value) : field.value) : defaultValue;
      }
      return defaultValue;
    };

    return {
      clientSubacc: getFieldValue('clientSubacc', CCBILL_SUBACCOUNT),
      currencyCode: getFieldValue('currencyCode', CCBILL_CURRENCY_CODE),
      initialPrice: getFieldValue('initialPrice', 0),
      initialPeriod: getFieldValue('initialPeriod', 0),
      recurringPrice: getFieldValue('recurringPrice', null),
      recurringPeriod: getFieldValue('recurringPeriod', null),
      numRebills: getFieldValue('numRebills', null)
    };
  }, []);

  /**
   * Charge tip with existing payment token
   * @param {string} paymentTokenId - Payment token ID
   * @param {Object} tipData - Tip data
   * @param {string} contentId - Content ID
   * @param {Array|Object} form - Payment form data
   */
  const chargeTipWithExistingToken = useCallback(async (paymentTokenId, tipData, contentId, form, onResolveSuccess) => {
    try {
      showPaymentLoading();

      const passThroughInfo = extractPassThroughInfo(form, contentId, tipData);
      const formValues = extractPaymentFormValues(form);

      const chargePayload = {
        paymentTokenId,
        clientSubacc: formValues.clientSubacc,
        currencyCode: formValues.currencyCode,
        initialPrice: tipData.amount,
        initialPeriod: formValues.initialPeriod,
        passThroughInfo
      };

      if (formValues.recurringPrice) chargePayload.recurringPrice = Number(formValues.recurringPrice);
      if (formValues.recurringPeriod) chargePayload.recurringPeriod = Number(formValues.recurringPeriod);
      if (formValues.numRebills) chargePayload.numRebills = Number(formValues.numRebills);

      const chargeResponse = await chargeTip(chargePayload);

      if (chargeResponse.success) {
        tipSuccessfulRef.current = true;
        const tipMessage = `Sent a tip of $${tipData.amount}`;
        const tipMeta = buildMessageTipMeta(tipData, extractTipId(form));
        onResolveSuccess?.(tipMessage, tipMeta);

        closeAlert();
        showTipSentPushToast(tipData.amount, creatorInfo);
        if (toggle) toggle();
        if (onTipSuccess && tipData.amount) {
          if (tipMeta) {
            onTipSuccess(tipMessage, tipMeta);
          } else {
            onTipSuccess(tipMessage);
          }
        }
      } else {
        closeAlert();
        showTipFailedPushToast(TIP_PAYMENT_FAILED_TITLE, TIP_PAYMENT_FAILED_MESSAGE);
      }
    } catch (error) {
      closeAlert();
      showTipFailedPushToast(TIP_PAYMENT_FAILED_TITLE, TIP_PAYMENT_FAILED_MESSAGE);
    }
  }, [extractPassThroughInfo, extractPaymentFormValues, extractTipId, toggle, onTipSuccess, chargeTip, creatorInfo]);

  /**
   * Show tip payment confirmation dialog
   * @param {string} paymentTokenId - Payment token ID
   * @param {Object} tipData - Tip data
   * @param {string} contentId - Content ID
   * @param {Array|Object} form - Payment form data
   */
  const showTipPaymentConfirmation = useCallback(async (paymentTokenId, tipData, contentId, form, resolveSuccess, resolveFailure) => {
    const confirmed = await confirmDialog(
      'Confirm Payment',
      `You have a saved payment method. Would you like to send a tip for $${formatAmountWithTwoDecimalPlaces(tipData?.amount)}?`,
      {
        confirmButtonText: 'Send Tip',
        cancelButtonText: 'Cancel',
        cancelButtonColor: '#d33',
        ...SWAL_TIP_CONFIG
      }
    );

    if (confirmed) {
      chargeTipWithExistingToken(paymentTokenId, tipData, contentId, form, resolveSuccess);
    } else {
      const tipId = extractTipId(form) || currentTipId;
      if (tipId) {
        cancelTip(tipId, true, false);
      }
      closeAlert();
      resolveFailure?.();
    }
  }, [chargeTipWithExistingToken, extractTipId, currentTipId, cancelTip]);

  /**
   * Send tip payment
   * @param {string} id - Content or message ID
   * @param {string} tipType - Tip type (content or messages)
   */
  const updatePurchaseDetails = useCallback(
    async (rawAmount) => {
      const parsedAmount = parseFloat(rawAmount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setVatDisplay(0);
        setVatPercentage(0);
        return null;
      }

      const contentPrice = (parsedAmount * ADM_TIP_PERCENTAGE).toFixed(2);
      const purchasePayload = await buildTipPayload(contentPrice, parsedAmount, user);
      setTotalTipValue(purchasePayload?.amount ?? 0);

      setVatDisplay(formatAmountWithTwoDecimalPlaces(purchasePayload?.vatAmount) ?? 0);
      setVatPercentage(purchasePayload?.vatPercentage ?? 0);

      return purchasePayload;
    },
    [user]
  );

  const sendTip = useCallback(async (id, tipType = TIP_TYPE.CONTENT, amountOverride = undefined) => {
    const amountToUse = amountOverride !== undefined && amountOverride !== null ? parseFloat(amountOverride) : parseFloat(tipValue);
    const parsedAmount = amountToUse;

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      showTipFailedPushToast('Invalid Amount', 'Please enter a valid tip amount.');
      return { success: false };
    }

    if (parsedAmount < MIN_VALUE_TIPS || parsedAmount > MAX_VALUE_TIPS) {
      showTipFailedPushToast('Amount Out of Range', `Minimum tip amount is $${MIN_VALUE_TIPS} and maximum is $${MAX_VALUE_TIPS}`);
      return { success: false };
    }

    if (!accessToken) {
      showTipFailedPushToast('Authentication Required', 'Please login to send a tip.');
      return { success: false };
    }

    const origin = window.location.origin.toString();
    const purchasePayload = await updatePurchaseDetails(parsedAmount);
    if (!purchasePayload) {
      showTipFailedPushToast('Error', 'Unable to prepare payment details. Please try again.');
      return { success: false };
    }
    purchasePayload.tip_type = tipType;
    if (isMessagesTip) {
      purchasePayload.returnUrl = `${origin}/my/messages/chat/${id}`;

      // IMPORTANT: read latest composer / pending message directly from ref so
      const liveRawMessage =
        (typeof openTipModalRef?.current?.composerMessageForTip === 'string' &&
          openTipModalRef.current.composerMessageForTip) ||
        (typeof openTipModalRef?.current?.pendingMessageWithTip === 'string' &&
          openTipModalRef.current.pendingMessageWithTip) ||
        '';
      const liveMessageWithTip = liveRawMessage.trim();
      const hasLiveMessageWithTip = Boolean(liveMessageWithTip);

      if (hasLiveMessageWithTip) {
        const trimmed = liveMessageWithTip;
        purchasePayload.message = trimmed;
      }
    }
    if (openTipModalRef?.current?.isFirstMessageTip) {
      purchasePayload.isFirstMessageTip = true;
      delete openTipModalRef.current.isFirstMessageTip;
    }

    return new Promise((resolve) => {
      pendingTipResolveRef.current = resolve;
      const resolveSuccess = (tipMessage, tipMeta) => {
        if (pendingTipResolveRef.current) {
          pendingTipResolveRef.current({ success: true, tipMessage, tipMeta });
          pendingTipResolveRef.current = null;
        }
      };
      const resolveFailure = () => {
        if (pendingTipResolveRef.current) {
          pendingTipResolveRef.current({ success: false });
          pendingTipResolveRef.current = null;
        }
      };

      showPaymentLoading();

      (async () => {
        try {
          let existingPaymentTokenId = null;
          try {
            const accountResponse = await userService.fetchUser();
            if (accountResponse?.success && accountResponse?.data?.paymentTokenId) {
              existingPaymentTokenId = accountResponse.data.paymentTokenId;
            }
          } catch (error) {
            reportWarning(error, 'sendTip fetch account');
          }

          const res = await sendTipAction(id, purchasePayload, isMessagesTip);
          if (res.success && res.data) {
            const paymentForm = res.data?.paymentForm;

            const tipId = extractTipId(paymentForm);
            if (tipId) {
              setCurrentTipId(tipId);
            }

            const paymentTokenFromResponse = Array.isArray(paymentForm)
              ? paymentForm.find(f => f.name === 'paymentTokenId')?.value
              : paymentForm?.fields?.find(f => f.name === 'paymentTokenId')?.value;

            if (paymentForm && res?.data?.paymentGateway === 'CCBILL') {
              if (res.data?.paymentOneClick == 1) {
                tipSuccessfulRef.current = true;

                closeAlert();
                showTipSentPushToast(purchasePayload.amount, creatorInfo);
                const tipMessage = `Sent a tip of $${purchasePayload.amount}`;
                const tipMeta = buildMessageTipMeta(purchasePayload, tipId);
                if (onTipSuccess && purchasePayload.amount) {
                  if (tipMeta) {
                    onTipSuccess(tipMessage, tipMeta);
                  } else {
                    onTipSuccess(tipMessage);
                  }
                }
                resolveSuccess(tipMessage, tipMeta);
                window.location.href = res.data.paymentUrl;
              } else {
                const paymentTokenId = paymentTokenFromResponse || existingPaymentTokenId;
                const hasValidPaymentToken = Boolean(
                  paymentTokenId !== null &&
                  paymentTokenId !== undefined &&
                  paymentTokenId !== '' &&
                  paymentTokenId !== 'null' &&
                  paymentTokenId !== 'undefined'
                );

                if (hasValidPaymentToken) {
                  showTipPaymentConfirmation(paymentTokenId, purchasePayload, id, paymentForm, resolveSuccess, resolveFailure);
                } else {
                  setPaymentForm(paymentForm);
                  setCcBillModalOpen(true);
                  closeAlert();
                }
              }
            } else if (paymentForm && res?.data?.paymentGateway === 'SEGPAY') {
              if (res.data.paymentUrl) {
                window.location.href = res.data.paymentUrl;
              }
              closeAlert();
              if (res.data.paymentOneClick === 1) {
                tipSuccessfulRef.current = true;

                showTipSentPushToast(purchasePayload.amount, creatorInfo);
                const tipMessage = `Sent a tip of $${purchasePayload.amount}`;
                const tipMeta = buildMessageTipMeta(purchasePayload, tipId);
                if (onTipSuccess && purchasePayload.amount) {
                  if (tipMeta) {
                    onTipSuccess(tipMessage, tipMeta);
                  } else {
                    onTipSuccess(tipMessage);
                  }
                }
                resolveSuccess(tipMessage, tipMeta);
              } else {
                resolveFailure();
              }
            } else {
              closeAlert();
              showTipFailedPushToast('Payment Error', 'Unable to process payment. Please try again.');
              resolveFailure();
            }
          } else {
            const errorMessage = res.error || res.message || 'Failed to process tip. Please try again.';
            closeAlert();
            showTipFailedPushToast('Error', errorMessage);
            resolveFailure();
          }
        } catch (error) {
          const statusCode = error?.response?.status;
          const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred. Please try again.';

          closeAlert();
          if (statusCode === 401 || statusCode === 403) {
            showTipFailedPushToast('Authentication Error', 'Please login to send a tip.');
          } else if (statusCode === 422) {
            showTipFailedPushToast('Validation Error', errorMessage);
          } else {
            showTipFailedPushToast('Error', errorMessage);
          }
          resolveFailure();
        }
      })();
    });
  }, [tipValue, isMessagesTip, accessToken, extractTipId, showTipPaymentConfirmation, onTipSuccess, sendTipAction, updatePurchaseDetails, creatorInfo]);

  useEffect(() => {
    if (openTipModalRef?.current && contentId) {
      openTipModalRef.current.sendTipWithAmount = async (amount) =>
        sendTip(contentId, tip_type, amount);
      return () => {
        if (openTipModalRef?.current) {
          openTipModalRef.current.sendTipWithAmount = null;
        }
      };
    }
  }, [openTipModalRef, contentId, tip_type, sendTip]);

  /**
   * Update slider background based on value
   * @param {number} value - Tip value
   */
  const updateSliderBackground = useCallback((value) => {
    const percentage = ((value - MIN_VALUE_TIPS) / (MAX_VALUE_TIPS - MIN_VALUE_TIPS)) * 100;
    const slider = document.querySelector('.jh-modern-tip-slider');
    if (slider) {
      slider.style.setProperty('--jh-tip-slider-value', percentage + '%');
    }
  }, []);

  /**
   * Handle input change for tip value
   * @param {Event} event - Input change event
   */
  const handleInputChange = useCallback((event) => {
    const value = event.target.value;
    if (value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setTipValue(numValue);
        updateSliderBackground(numValue);
      }
    } else {
      setTipValue(0);
      setTotalTipValue(0);
      updateSliderBackground(0);
    }
  }, [updateSliderBackground]);

  /**
   * Set preset tip amount
   * @param {number} amount - Preset amount
   */
  const setPresetTipAmount = useCallback((amount) => {
    setTipValue(amount);
    updateSliderBackground(amount);
  }, [updateSliderBackground]);

  // Update total tip value when tip value changes
  useEffect(() => {
    const parsedValue = parseFloat(tipValue) || 0;
    // setTotalTipValue((parsedValue * ADM_TIP_PERCENTAGE).toFixed(2));
    updateSliderBackground(parsedValue);
  }, [tipValue, updateSliderBackground]);

  // Initialize slider background on mount
  useEffect(() => {
    if (!isOpen) {
      setVatDisplay(0);
      setVatPercentage(0);
      return;
    }

    updatePurchaseDetails(tipValue);
    const timeoutId = setTimeout(() => {
      updateSliderBackground(tipValue);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isOpen, tipValue, updateSliderBackground, updatePurchaseDetails]);

  /**
   * Handle CCBill modal toggle (e.g. X or overlay click) – return to tip screen
   */
  const handleCCBillModalToggle = useCallback(() => {
    setCcBillModalOpen(false);
    setPaymentForm(null);

    if (!tipSuccessfulRef.current && pendingTipResolveRef.current) {
      pendingTipResolveRef.current({ success: false });
      pendingTipResolveRef.current = null;
    }
    setTimeout(() => {
      if (!tipSuccessfulRef.current) {
        const tipId = extractTipId(paymentForm) || currentTipId;
        if (tipId) {
          cancelTip(tipId, false, false);
        }
      }
    }, 150);
  }, [paymentForm, currentTipId, extractTipId, cancelTip]);

  /**
   * Handle CCBill modal cancel – return to tip screen
   */
  const handleCCBillModalCancel = useCallback(() => {
    const tipId = extractTipId(paymentForm) || currentTipId;

    setCcBillModalOpen(false);
    setPaymentForm(null);

    if (!tipSuccessfulRef.current && pendingTipResolveRef.current) {
      pendingTipResolveRef.current({ success: false });
      pendingTipResolveRef.current = null;
    }
    if (tipId) {
      setTimeout(() => {
        cancelTip(tipId, true, false);
      }, 100);
    }
  }, [paymentForm, currentTipId, extractTipId, cancelTip]);

  /**
   * Handle CCBill modal success
   */
  const handleCCBillModalSuccess = useCallback(() => {
    tipSuccessfulRef.current = true;
    setCcBillModalOpen(false);
    setPaymentForm(null);
    const tipMessage = `Sent a tip of $${totalTipValue}`;
    const tipMeta = isMessagesTip
      ? {
        message: messageWithTip || null,
        isFirstMessageTip: Boolean(openTipModalRef?.current?.isFirstMessageTip),
        amount: totalTipValue,
        vatAmount: vatDisplay,
        vatPercentage,
        baseAmount: null,
        realPrice: null,
        extraCharge: null,
        platformFee: null,
        tip_type: tip_type,
        creatorId: creatorInfo?._id ?? null,
        tipId: currentTipId,
      }
      : null;
    if (pendingTipResolveRef.current) {
      pendingTipResolveRef.current({ success: true, tipMessage, tipMeta });
      pendingTipResolveRef.current = null;
    }
    if (toggle) toggle();
    if (onTipSuccess && totalTipValue) {
      if (tipMeta) {
        onTipSuccess(tipMessage, tipMeta);
      } else {
        onTipSuccess(tipMessage);
      }
    }
  }, [toggle, onTipSuccess, totalTipValue, isMessagesTip, messageWithTip, openTipModalRef, vatDisplay, vatPercentage, tip_type]);



  const modalRoot = (typeof document !== 'undefined' && document.body) || null;

  return (
    <>
      {isOpen &&
        modalRoot &&
        createPortal(
          <div className="jh-modern-tip-overlay cm_bg_blur">
            <div className="jh-modern-tip-popup">
              <div className="jh-modern-tip-container">
                {/* Header with creator info */}
                <div className="jh-modern-tip-header">
                  <div className="jh-modern-tip-avatar-container">
                    <img
                      src={creatorInfo?.profile_picture}
                      alt="Creator Avatar"
                      className="jh-modern-tip-avatar"
                    />
                    <div className="jh-modern-tip-avatar-gradient"></div>
                  </div>
                  <div className="jh-modern-tip-creator-info">
                    <div className="jh-modern-tip-username">@{creatorInfo?.username}</div>
                    <div className="jh-modern-tip-support-text">
                      <i className="fa-solid fa-star jh-modern-tip-star-icon"></i>
                      Support this creator
                    </div>
                  </div>
                </div>

                {/* Tip amount section */}
                <div className="jh-modern-tip-amount-section">
                  <div className="jh-modern-tip-amount-header">
                    <span className="jh-modern-tip-amount-label">Tip Amount</span>
                    <div className="jh-modern-tip-amount-display">
                      ${formatAmountWithTwoDecimalPlaces(totalTipValue)}
                    </div>
                  </div>

                  {/* Custom slider */}
                  <div className="jh-modern-tip-slider-container">
                    <input
                      type="range"
                      step="0.01"
                      min={MIN_VALUE_TIPS}
                      max={MAX_VALUE_TIPS}
                      value={tipValue}
                      onChange={handleInputChange}
                      className="jh-modern-tip-slider"
                    />

                    <div className="jh-modern-tip-slider-labels">
                      <span className="jh-modern-tip-slider-label">
                        ${formatAmountWithTwoDecimalPlaces(formatCurrency(MIN_VALUE_TIPS * ADM_TIP_PERCENTAGE))}
                      </span>
                      <span className="jh-modern-tip-slider-label">
                        ${formatAmountWithTwoDecimalPlaces(formatCurrency((MAX_VALUE_TIPS * ADM_TIP_PERCENTAGE) / 2))}
                      </span>
                      <span className="jh-modern-tip-slider-label">
                        ${formatAmountWithTwoDecimalPlaces(formatCurrency((MAX_VALUE_TIPS * ADM_TIP_PERCENTAGE)))}
                      </span>
                    </div>
                  </div>

                  {/* Preset buttons */}
                  <div className="jh-modern-tip-preset-buttons">
                    {DEFAULT_PRESET_AMOUNTS.map((amount) => (
                      <button
                        key={amount}

                        className={`jh-modern-tip-preset-btn ${amount == tipValue ? 'active' : ''}`}
                        onClick={() => setPresetTipAmount(amount)}
                        type="button"
                      >
                        ${(amount * ADM_TIP_PERCENTAGE).toFixed(0)}
                      </button>
                    ))}
                  </div>

                  {/* Message preview: only when subscriber typed a message and opened tip (same as GetNoticedEmptyState message-with-tip) */}
                  {isMessagesTip && hasMessageWithTip && (
                    <div className="jh-modern-tip-message-preview">
                      <p className="jh-modern-tip-message-text">
                        {messageWithTip.length > MESSAGES_TIP_PREVIEW_LENGTH
                          ? (messageExpanded
                            ? messageWithTip
                            : truncateMessage(messageWithTip, MESSAGES_TIP_PREVIEW_LENGTH))
                          : messageWithTip}
                      </p>
                      {messageWithTip.length > MESSAGES_TIP_PREVIEW_LENGTH && (
                        <button
                          type="button"
                          className="jh-modern-tip-message-toggle"
                          onClick={() => setMessageExpanded((prev) => !prev)}
                        >
                          {messageExpanded ? 'show less' : 'show more'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="jh-modern-tip-vat-info mb-1">
                  <p className="jh-modern-tip-security-text" style={{ marginBottom: '12px' }}>
                    {getVatAndServiceFeeAppliedText(vatDisplay ?? 0)}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="jh-modern-tip-actions">
                  {creatorInfo?.old_account !== true ? (
                    <button
                      onClick={() => {
                        if (hasMessageWithTip && openTipModalRef?.current) {
                          openTipModalRef.current.pendingMessageWithTip = messageWithTip;
                        }
                        sendTip(contentId, tip_type);
                      }}
                      className="jh-modern-tip-send-btn"
                      type="button"
                    >
                      <i className="fa-solid fa-heart jh-modern-tip-heart-icon"></i>
                      {hasMessageWithTip ? 'Send Tip & Message' : 'Send Tip'}
                    </button>
                  ) : null}
                  <button
                    onClick={() => {
                      if (toggle) {
                        toggle();
                      }
                    }}
                    className="jh-modern-tip-cancel-btn"
                    type="button"
                  >
                    Cancel
                  </button>
                </div>



                {/* Footer */}
                <div className="jh-modern-tip-footer">
                  <p className="jh-modern-tip-security-text">
                    <i className="fa-solid fa-shield-halved jh-modern-tip-shield-icon"></i>
                    Secure payment powered by CCBill
                  </p>
                </div>
              </div>
            </div>
          </div>,
          modalRoot
        )}
      {ccBillModalOpen && paymentForm && (
        <CCBillModal
          paymentForm={paymentForm}
          openModal={ccBillModalOpen}
          toggleModal={handleCCBillModalToggle}
          onCancel={handleCCBillModalCancel}
          onSuccess={handleCCBillModalSuccess}
          purchaseAmount={totalTipValue}
          vatAmount={vatDisplay}
          vatPercentage={vatPercentage}
          creatorInfo={creatorInfo}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
});

export default connect(mapStateToProps)(TipModal);